import {
  useListTabularConfigBuildsQuery,
  type IngestMappingConfigSummary,
  type TabularConfigBuildDetail
} from '@axilon-apps/domains-ontology-ui-state'
import { Link } from 'react-router-dom'
import { Card } from '../../../components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import { COMPACT_TABLE } from '../../IngestPage/shared/compact'
import { tabularConfigBuildPath, tabularConfigPath } from '../../TabularIngestJobPage/route'
import { isBuildInFlight, newestFirst } from '../util/configView'
import { formatWhen, reasonText, shortId } from '../util/formatMeta'

type HistoryEntry =
  | { kind: 'version'; at: string; row: IngestMappingConfigSummary }
  | { kind: 'closed_draft'; at: string; build: TabularConfigBuildDetail }

/**
 * Every version, newest first, with the build in flight on top and the
 * drafts that were sent back or rejected between them. Runs are not here:
 * uploads are in *Runs* (Type column) and intake files on the intake's ledger.
 */
export function HistoryTab({
  name,
  versions,
  inFlight,
  currentVersion,
  navState
}: {
  name: string
  versions: IngestMappingConfigSummary[]
  inFlight: TabularConfigBuildDetail | null
  /** The version the page shows, so its row can say so. */
  currentVersion?: number
  /** `location.state` to carry on the links, so Back keeps leading out of the config. */
  navState?: unknown
}) {
  const draft = inFlight && isBuildInFlight(inFlight) ? inFlight : null
  const ready = draft?.result.phase === 'awaiting_review' && !!draft.result.draft
  const rows = newestFirst(versions)
  /** The version saved just before `version`, whatever its number or state. */
  const previousOf = (version: number) => rows.find((row) => row.version < version)
  const builds = useListTabularConfigBuildsQuery(name, { skip: !name }).data ?? []
  const entries: HistoryEntry[] = [
    ...rows.map((row) => ({ kind: 'version' as const, at: row.created_at, row })),
    ...builds.filter(isClosedDraft).map((build) => ({
      kind: 'closed_draft' as const,
      at: build.completedAt ?? build.updatedAt,
      build
    }))
  ].sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0))
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Card className="min-w-0 overflow-hidden p-0">
        <Table className={COMPACT_TABLE}>
          <TableHeader>
            <TableRow>
              <TableHead>Version</TableHead>
              <TableHead>Saved</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="w-2/5">Reason</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft ? (
              <TableRow data-testid="history-draft-row" className="bg-st-warn-bg/40">
                <TableCell className="font-semibold whitespace-nowrap">
                  draft v{draft.proposedVersion}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {ready ? formatRelativeTime(draft.updatedAt) : 'building…'}
                </TableCell>
                <TableCell>builder</TableCell>
                <TableCell className="text-ink-2">{reasonText(draft.reason)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {ready ? (draft.result.draft?.verification.status ?? '—') : '—'}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Link
                    className="underline"
                    to={tabularConfigBuildPath(name, draft.jobId)}
                    state={navState}
                  >
                    {ready ? 'review' : 'watch'}
                  </Link>
                </TableCell>
              </TableRow>
            ) : null}
            {entries.map((entry) => {
              if (entry.kind === 'closed_draft') {
                return (
                  <ClosedDraftRow
                    key={entry.build.jobId}
                    name={name}
                    build={entry.build}
                    navState={navState}
                  />
                )
              }
              const { row } = entry
              const { parser } = row.definition
              const archived = row.state === 'archived'
              const shown = row.version === currentVersion
              const previous = previousOf(row.version)
              return (
                <TableRow key={row.id} className={cn(archived && 'text-ink-3')}>
                  <TableCell className="whitespace-nowrap">
                    <span className="font-semibold">v{row.version}</span>
                    {shown ? <span className="ml-1.5 text-11 text-ink-3">shown</span> : null}
                    {archived ? (
                      <span className="ml-1.5 text-11">
                        archived{row.archived_at ? ` ${formatWhen(row.archived_at)}` : ''}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap" title={formatWhen(row.created_at)}>
                    {formatRelativeTime(row.created_at, { fallback: row.created_at })}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{parser.approvedBy}</TableCell>
                  <TableCell className="text-ink-2">{parser.changeReason}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {parser.kind === 'script' ? formatWhen(parser.verifiedAt) : '—'}
                  </TableCell>
                  <TableCell className="space-x-3 text-right whitespace-nowrap">
                    <Link
                      className="underline"
                      to={tabularConfigPath(name, { version: row.version })}
                      state={navState}
                    >
                      view
                    </Link>
                    {previous ? (
                      <Link
                        className="underline"
                        to={tabularConfigPath(name, {
                          version: row.version,
                          compare: previous.version,
                          tab: parser.kind === 'script' ? 'script' : 'description'
                        })}
                        state={navState}
                      >
                        diff to previous
                      </Link>
                    ) : null}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>
      <p className="text-11 text-ink-3">
        Uploads with this config are in <em>Runs</em> on the Ingest page (Type column). Files sent
        to an intake are on that intake under <em>Intake endpoints</em>.
      </p>
    </div>
  )
}

function isClosedDraft(build: TabularConfigBuildDetail): boolean {
  const { disposition } = build.result
  return disposition === 'superseded' || disposition === 'rejected'
}

function ClosedDraftRow({
  name,
  build,
  navState
}: {
  name: string
  build: TabularConfigBuildDetail
  navState?: unknown
}) {
  const { result } = build
  const at = build.completedAt ?? build.updatedAt
  const supersededBy = result.revision?.supersededBy
  return (
    <TableRow data-testid="history-closed-draft-row" className="text-ink-3">
      <TableCell className="whitespace-nowrap">draft v{build.proposedVersion}</TableCell>
      <TableCell className="whitespace-nowrap" title={formatWhen(at)}>
        {formatRelativeTime(at, { fallback: at })}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {(result.disposition === 'superseded'
          ? result.revision?.revisedBy
          : result.rejection?.rejectedBy) ?? '—'}
      </TableCell>
      <TableCell>
        {result.disposition === 'superseded' ? (
          supersededBy ? (
            <>
              superseded →{' '}
              <Link
                className="underline"
                to={tabularConfigBuildPath(name, supersededBy)}
                state={navState}
              >
                build {shortId(supersededBy)}
              </Link>
            </>
          ) : (
            'superseded'
          )
        ) : (
          `rejected: “${result.rejection?.note ?? ''}”`
        )}
      </TableCell>
      <TableCell>—</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Link className="underline" to={tabularConfigBuildPath(name, build.jobId)} state={navState}>
          view
        </Link>
      </TableCell>
    </TableRow>
  )
}
